import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, ImagePlus, ChevronLeft, ChevronRight, Trash2, FileCheck2, Video, Play } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Switch } from "./ui/switch";
import { api, errMsg } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { photoUrl, fmtDate } from "../lib/helpers";
import { Field, Empty } from "./common";
import { AISuggestion } from "./media/AISuggestion";
import { uploadVideo, MAX_VIDEO } from "./media/videoUpload";

const Media = ({ p, className, controls }) => p.media_type === "video"
  ? <video src={photoUrl(p.id)} className={className} controls={controls} muted={!controls} playsInline preload="metadata" data-testid={`video-${p.id}`} />
  : <img src={photoUrl(p.id)} alt={p.caption || ""} loading="lazy" className={className} />;

export const CATS = {
  estimate: ["Estimate Photos", "Fotos del presupuesto"], before: ["Before", "Antes"], during: ["During", "Durante"],
  issues: ["Issues / Damage", "Problemas / Daños"], change_order: ["Change Order", "Orden de cambio"], after: ["After", "Después"],
  warranty: ["Warranty", "Garantía"], general: ["Customer Photos", "Fotos del cliente"],
};
export const TIMELINE = ["estimate", "before", "during", "issues", "change_order", "after", "warranty"];

function UploadButtons({ cat, fields, onDone, addLabel }) {
  const { t } = useLang();
  const cam = useRef();
  const lib = useRef();
  const vid = useRef();
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(null);
  const upVideo = async (file) => {
    if (!file) return;
    if (file.size > MAX_VIDEO) { vid.current.value = ""; return toast.error(t("Video must be under 100 MB", "El video debe pesar menos de 100 MB")); }
    setPct(0);
    try { await uploadVideo(file, { ...fields, category: cat }, setPct); toast.success(t("Video added", "Video agregado")); onDone(); } catch (e) { toast.error(errMsg(e)); } finally { setPct(null); vid.current.value = ""; }
  };
  const upload = async (files) => {
    if (!files?.length) return;
    const fd = new FormData();
    [...files].forEach((f) => fd.append("files", f));
    fd.append("category", cat);
    Object.entries(fields).forEach(([k, v]) => v && fd.append(k, v));
    setBusy(true);
    try {
      const { data } = await api.post("/photos", fd);
      toast.success(`${data.length} ${t("photo(s) added", "foto(s) agregada(s)")}`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); cam.current.value = ""; lib.current.value = ""; }
  };
  return (
    <div className={`grid gap-2 ${fields.project_id ? "grid-cols-3" : "grid-cols-2"}`}>
      {fields.project_id && <input ref={vid} type="file" accept="video/*" hidden onChange={(e) => upVideo(e.target.files?.[0])} data-testid={`video-input-${cat}`} />}
      <input ref={cam} type="file" accept="image/*" capture="environment" hidden onChange={(e) => upload(e.target.files)} data-testid={`photo-camera-input-${cat}`} />
      <input ref={lib} type="file" accept="image/*" multiple hidden onChange={(e) => upload(e.target.files)} data-testid={`photo-input-${cat}`} />
      <button className="btn-primary h-14" disabled={busy} onClick={() => lib.current.click()} data-testid={`photo-upload-${cat}-btn`}><ImagePlus className="w-5 h-5" /> {busy ? t("Uploading…", "Subiendo…") : addLabel || t("+ Add Photos", "+ Agregar fotos")}</button>
      <button className="btn-outline h-14" disabled={busy} onClick={() => cam.current.click()} data-testid={`photo-take-${cat}-btn`}><Camera className="w-5 h-5" /> {t("Take Photo", "Tomar foto")}</button>
      {fields.project_id && <button className="btn-outline h-14" disabled={busy || pct !== null} onClick={() => vid.current.click()} data-testid={`video-upload-${cat}-btn`}><Video className="w-5 h-5" /> {pct !== null ? `${pct}%` : t("Video", "Video")}</button>}
    </div>
  );
}

function PhotoEditor({ photo, includeToggle, portalToggle, onClose, onSaved, onMove }) {
  const { t, lang } = useLang();
  const { isStaff } = useAuth();
  const [f, setF] = useState({ caption: photo.caption || "", area: photo.area || "", internal_note: photo.internal_note || "", include_in_pdf: !!photo.include_in_pdf, portal_visible: !!photo.portal_visible, category: photo.category });
  const [cur, setCur] = useState(photo);
  const aiUpdate = (d) => { setCur(d); setF((x) => ({ ...x, internal_note: d.internal_note || "" })); onSaved(); };
  const save = async () => {
    try { await api.patch(`/photos/${photo.id}`, f); toast.success(t("Photo saved", "Foto guardada")); onSaved(); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => {
    if (!window.confirm(t("Delete this photo?", "¿Eliminar esta foto?"))) return;
    await api.delete(`/photos/${photo.id}`); onSaved(); onClose();
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[94vh] overflow-y-auto" data-testid="photo-editor-dialog">
        <DialogHeader><DialogTitle>{photo.media_type === "video" ? "Video" : t("Photo", "Foto")} · {fmtDate(photo.taken_date || photo.created_at, lang)}</DialogTitle></DialogHeader>
        {photo.media_type === "video" ? <Media p={photo} controls className="w-full max-h-[55vh] bg-slate-900 rounded-xl" />
          : <a href={photoUrl(photo.id)} target="_blank" rel="noreferrer"><img src={photoUrl(photo.id)} alt="" className="w-full max-h-[55vh] object-contain bg-slate-900 rounded-xl" data-testid="photo-editor-image" /></a>}
        <div className="text-xs text-slate-500">{t("Uploaded by", "Subida por")} {photo.uploaded_by_name}</div>
        <AISuggestion photo={cur} onUpdate={aiUpdate} />
        {isStaff ? (
          <div className="grid gap-3">
            <Field label={t("Caption / description", "Descripción")}><input className="inp" value={f.caption} onChange={set("caption")} placeholder={t("e.g. Stucco crack below front window", "ej. Grieta en el estuco debajo de la ventana")} data-testid="photo-caption-input" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("Area of property", "Área de la propiedad")}><input className="inp" value={f.area} onChange={set("area")} placeholder={t("Rear wall", "Pared trasera")} data-testid="photo-area-input" /></Field>
              <Field label={t("Category", "Categoría")}><select className="inp" value={f.category} onChange={set("category")} data-testid="photo-category-select">{Object.entries(CATS).map(([k, [en, es]]) => <option key={k} value={k}>{lang === "es" ? es : en}</option>)}</select></Field>
            </div>
            <Field label={t("Internal note (never shown to customer)", "Nota interna (nunca se muestra al cliente)")}><textarea className="inp !min-h-[64px]" value={f.internal_note} onChange={set("internal_note")} data-testid="photo-note-input" /></Field>
            {includeToggle && <label className="flex items-center justify-between rounded-xl border p-3 min-h-[52px]"><span className="font-semibold">{t("Include in PDF", "Incluir en el PDF")}</span><Switch checked={f.include_in_pdf} onCheckedChange={(v) => setF({ ...f, include_in_pdf: v })} data-testid="photo-include-switch" /></label>}
            {portalToggle && <label className="flex items-center justify-between rounded-xl border p-3 min-h-[52px]"><span className="font-semibold">{t("Show in customer portal", "Mostrar en portal del cliente")}</span><Switch checked={f.portal_visible} onCheckedChange={(v) => setF({ ...f, portal_visible: v })} data-testid="photo-portal-switch" /></label>}
            <div className="grid grid-cols-4 gap-2">
              <button className="btn-outline" onClick={() => onMove(-1)} data-testid="photo-move-left-btn" aria-label="Move left"><ChevronLeft className="w-5 h-5" /></button>
              <button className="btn-outline" onClick={() => onMove(1)} data-testid="photo-move-right-btn" aria-label="Move right"><ChevronRight className="w-5 h-5" /></button>
              <button className="btn-danger" onClick={del} data-testid="photo-delete-btn"><Trash2 className="w-5 h-5" /></button>
              <button className="btn-primary" onClick={save} data-testid="photo-save-btn">{t("Save", "Guardar")}</button>
            </div>
          </div>
        ) : (photo.caption && <p className="text-sm">{photo.caption} {photo.area && `· ${photo.area}`}</p>)}
      </DialogContent>
    </Dialog>
  );
}

export default function PhotoGallery({ filter, categories, uploadFields, uploadCats, includeToggle, portalToggle, timeline, hideEmptyCats, title, addLabel }) {
  const { t, lang } = useLang();
  const { can } = useAuth();
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null);
  const key = JSON.stringify(filter);
  const load = useCallback(() => api.get("/photos", { params: JSON.parse(key) }).then((r) => setItems(r.data)).catch(() => setItems([])), [key]);
  useEffect(() => { load(); }, [load]);
  if (!items) return null;
  const canUp = (c) => can("upload_photos") && (uploadCats || categories).includes(c);
  const move = async (dir) => {
    const list = items.filter((p) => p.category === edit.category);
    const i = list.findIndex((p) => p.id === edit.id);
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const all = [...items];
    const a = all.indexOf(list[i]);
    const b = all.indexOf(list[j]);
    [all[a], all[b]] = [all[b], all[a]];
    setItems(all);
    await api.post("/photos/reorder", { ids: all.map((p) => p.id) });
  };
  const quickInclude = async (p) => { await api.patch(`/photos/${p.id}`, { include_in_pdf: !p.include_in_pdf }); load(); };
  const shown = categories.filter((c) => !hideEmptyCats || canUp(c) || items.some((p) => p.category === c));
  return (
    <div className="grid gap-4">
      {timeline && (
        <div className="flex flex-wrap items-center gap-1" data-testid="photo-timeline">
          {shown.map((c, i) => (
            <div key={c} className="flex items-center gap-1 shrink-0">
              {i > 0 && <ChevronRight className="w-4 h-4 text-slate-300" />}
              <button onClick={() => document.getElementById(`photo-cat-${c}`)?.scrollIntoView({ behavior: "smooth", block: "start" })} className="btn-sm btn bg-white border border-slate-300" data-testid={`photo-timeline-${c}`}>
                {CATS[c][lang === "es" ? 1 : 0]} <span className="mono text-xs bg-slate-900 text-white rounded-full px-1.5">{items.filter((p) => p.category === c).length}</span>
              </button>
            </div>
          ))}
        </div>
      )}
      {shown.map((c) => {
        const list = items.filter((p) => p.category === c);
        return (
          <section key={c} id={`photo-cat-${c}`} className="card p-4 scroll-mt-20" data-testid={`photo-section-${c}`}>
            <h3 className="font-bold mb-3">{title || CATS[c][lang === "es" ? 1 : 0]} <span className="mono text-xs text-slate-400">({list.length})</span></h3>
            {canUp(c) && <div className="mb-3"><UploadButtons cat={c} fields={uploadFields || {}} onDone={load} addLabel={addLabel} /></div>}
            {!list.length ? <Empty text={t("No photos yet", "Sin fotos todavía")} /> : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {list.map((p) => (
                  <div key={p.id} className="rounded-xl border border-slate-200 overflow-hidden bg-white" data-testid={`photo-${p.id}`}>
                    <button className="relative block w-full aspect-[4/3] bg-slate-100" onClick={() => setEdit(p)} data-testid={`photo-open-${p.id}`}><Media p={p} className="w-full h-full object-cover pointer-events-none" />{p.media_type === "video" && <span className="absolute inset-0 grid place-items-center"><Play className="w-10 h-10 text-white drop-shadow-lg fill-white/80" /></span>}{p.ai_suggestion?.status === "pending" && <span className="absolute top-1 right-1 text-[10px] font-bold bg-[#D9531E] text-white rounded px-1.5 py-0.5">IA</span>}</button>
                    <div className="p-2">
                      <div className="text-xs font-semibold truncate">{p.caption || <span className="text-slate-400">{t("No caption", "Sin descripción")}</span>}</div>
                      <div className="text-[11px] text-slate-500 truncate">{[p.area, fmtDate(p.taken_date || p.created_at, lang)].filter(Boolean).join(" · ")}</div>
                      {includeToggle && (
                        <button onClick={() => quickInclude(p)} className={`mt-1.5 w-full min-h-[36px] rounded-lg text-xs font-bold flex items-center justify-center gap-1 ${p.include_in_pdf ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"}`} data-testid={`photo-include-${p.id}`}>
                          <FileCheck2 className="w-4 h-4" /> {p.include_in_pdf ? t("In PDF", "En PDF") : t("Include in PDF", "Incluir en PDF")}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        );
      })}
      {edit && <PhotoEditor photo={edit} includeToggle={includeToggle} portalToggle={portalToggle} onClose={() => setEdit(null)} onSaved={load} onMove={move} />}
    </div>
  );
}
